import { Dictionary } from 'application/adapters/context/Dictionary';

/**
 * Utilized in Smart Lab
 * Gets formatted error message for equipment error codes
 * Error codes are stable identifiers that map directly to dictionary keys
 *
 * Dictionary lookup, tried in order:
 * 1. SmartLab.{Model}Code{errorCode}TypeError   → "SmartLab.CitopressCode50TypeError"
 * 2. SmartLab.{Model}Code{errorCode}TypeMessage → "SmartLab.CitopressCode50TypeMessage"
 * 3. SmartLab.{Model}Code{errorCode}TypeWarning → "SmartLab.CitopressCode50TypeWarning"
 * 4. SmartLab.{Model}Code{errorCode}            → "SmartLab.CitopressCode50" (untyped, defaults to "warning")
 *
 * The type lives in the key alias rather than a translated value, since it's an internal category
 * (not user-facing text) and aliases aren't per-culture — this avoids asking content editors to
 * "translate" the word "error"/"warning"/"message" into every locale. Content editors can migrate a
 * code to an explicit type at their own pace by adding one of the three typed keys; untouched codes
 * keep working via the plain key.
 *
 * If none of the four keys exist, the error is treated as unrecognized and nothing is shown — there is
 * no generic "unknown error" fallback text.
 *
 * @param errorCode - Equipment error code (e.g., "43", "59"). Returns null for "0" (no error state), empty string, or whitespace.
 * @param dictionary - Dictionary context for translations
 * @param model - Equipment model name (e.g., "Citopress", "Disctotom", "Tegramin") for model-specific error messages
 * @returns Structured error message with prefix, description, and type, or null if no error or the code isn't found in the dictionary
 *
 * @example
 * // Explicitly typed error code found in CMS
 * getEquipmentErrorMessage("50", dictionary, "Citopress")
 * // Returns: { prefix: "Error 50", description: "Heating unit failure", type: "error" }
 *
 * @example
 * // Explicitly typed as "message" — uses the "Code" prefix instead of "Error"/"Warning"
 * getEquipmentErrorMessage("12", dictionary, "Citopress")
 * // Returns: { prefix: "Code 12", description: "Calibration in progress", type: "message" }
 *
 * @example
 * // Untyped error code (only the plain key exists) — defaults to "warning"
 * getEquipmentErrorMessage("43", dictionary, "Citopress")
 * // Returns: { prefix: "Warning 43", description: "Main supply voltage too low", type: "warning" }
 *
 */
export const isValidStepData = (current: number | null | undefined, total: number | null | undefined): boolean =>
	typeof current === 'number' && typeof total === 'number' && current > 0 && total > 0 && current <= total;

// These statuses have no meaningful method to show, so an "Info" placeholder is used instead
const HIDDEN_METHOD_STATUSES: Models.MachineStatus[] = ['idle', 'noConnection', 'offline'];

export const isMethodHidden = (status?: Models.MachineStatus | null): boolean => !!status && HIDDEN_METHOD_STATUSES.includes(status);

export type EquipmentErrorType = 'error' | 'message' | 'warning';

export interface EquipmentErrorMessage {
	prefix: string;
	description: string;
	type: EquipmentErrorType;
}

export const getEquipmentErrorMessage = (
	errorCode: string | null,
	dictionary: Partial<Dictionary>,
	model?: string | null,
): EquipmentErrorMessage | null => {
	if (!errorCode || !dictionary?.getValue || errorCode.trim() === '' || errorCode === '0' || !model?.trim()) return null;

	// dictionary.getValue() only returns null for a missing key when a falsy defaultValue is passed in
	// production — in development it returns a "[DICTIONARY ... MISSING]" debug string instead. A sentinel
	// default makes "not found" detectable the same way in both environments.
	const NOT_FOUND = '__equipment-error-code-not-found__';
	const baseKey = `SmartLab.${model}Code${errorCode}`;

	const TYPES: Record<EquipmentErrorType, { keySuffix: string; prefix: string }> = {
		error: { keySuffix: 'TypeError', prefix: 'Error' },
		message: { keySuffix: 'TypeMessage', prefix: 'Code' },
		warning: { keySuffix: 'TypeWarning', prefix: 'Warning' },
	};

	for (const type of Object.keys(TYPES) as EquipmentErrorType[]) {
		const description = dictionary.getValue(`${baseKey}${TYPES[type].keySuffix}`, [], NOT_FOUND);
		if (description && description !== NOT_FOUND) {
			return { prefix: `${TYPES[type].prefix} ${errorCode}`, description, type };
		}
	}

	// No explicitly-typed variant found — fall back to the plain (untyped) key, defaulting to
	// 'warning' (non-critical) so codes not yet classified by content editors stay calm.
	const description = dictionary.getValue(baseKey, [], NOT_FOUND);
	if (!description || description === NOT_FOUND) return null;

	return { prefix: `${TYPES.warning.prefix} ${errorCode}`, description, type: 'warning' };
};
